import { downloadTxt } from '@utils/Download'
import { formatElapsedMsToDuration, parseBackendDateTime, removeAccents } from '@utils/Format'
import { platformMeta } from '@modules/crm/constants/Transcript'

// Monta e baixa a transcrição completa de uma reunião em .txt — espelha o par
// exportBillingSheet.js (conteúdo) + downloadCsv (Blob) já existente no repo.

const FILE_NAME_PREFIX = 'transcricao'
const SLUG_FALLBACK = 'sem-titulo'
const SLUG_MAX_LENGTH = 80
const TITLE_FALLBACK = 'Sem título'
const UNKNOWN_SPEAKER = 'Desconhecido'
const EMPTY_VALUE = '-'
const SEPARATOR_CHAR = '='
const SEPARATOR_WIDTH = 56
const LINE_BREAK = '\r\n'
const LABEL_PAD_WIDTH = 15

/**
 * Slugifica o título da reunião pro nome do arquivo: sem acentos, minúsculo,
 * só [a-z0-9] com hífen único entre grupos, sem hífen nas pontas.
 * @param {String} title
 * @returns {String}
 */
export const slugifyTitle = (title) => {
  // removeAccents não tem guard de null — normalizar a entrada antes de chamar.
  const normalized = removeAccents(String(title || '')).toLowerCase()
  const slug = normalized
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX_LENGTH)
    .replace(/-+$/g, '') // o corte acima pode deixar um hífen pendurado
  return slug || SLUG_FALLBACK
}

/**
 * Formata uma fala como '[mm:ss] Locutor: texto', ou 'Locutor: texto' quando
 * o segmento não tem timestamp.
 * @param {Object} segment { speaker, text, timestamp }
 * @returns {String}
 */
export const formatSegmentLine = (segment) => {
  const speaker = (segment && segment.speaker) || UNKNOWN_SPEAKER
  const text = (segment && segment.text) || ''
  const timestamp = segment && segment.timestamp
  // Mesmo helper que a aba Transcrição usa (detail-tab-transcricao.vue) —
  // evita a tela e o arquivo divergirem no cálculo do tempo decorrido.
  const time = formatElapsedMsToDuration(timestamp)
  if (!time) return `${speaker}: ${text}`
  return `[${time}] ${speaker}: ${text}`
}

const joinParticipants = (participants) => {
  return (participants || [])
    .map((p) => (p && (p.nome || p.name)) || '')
    .filter(Boolean)
    .join(', ')
}

// Os computeds do modal devolvem a string EMPTY_VALUE ('-') quando o campo
// falta — tratar como ausente, senão o cabeçalho sai com 'Data:          -'.
const isEmptyHeaderValue = (value) => !value || value === EMPTY_VALUE

/**
 * Monta as linhas de metadado do cabeçalho (Data, Duração, Plataforma, ...),
 * já omitindo os campos ausentes.
 * @param {Object} params { transcript, dataFormatada, duracao }
 * @returns {Array<String>}
 */
export const buildTranscricaoHeaderLines = ({
  transcript,
  dataFormatada,
  duracao,
} = {}) => {
  const safeTranscript = transcript || {}
  const fields = [
    ['Data', dataFormatada],
    ['Duração', duracao],
    ['Plataforma', platformMeta(safeTranscript.platform).label],
    ['Cliente', safeTranscript.client_nome],
    ['Projeto', safeTranscript.project_name],
    ['Profissional', safeTranscript.profissional_nome],
    ['Participantes', joinParticipants(safeTranscript.participants)],
  ]
  return fields
    .filter(([, value]) => !isEmptyHeaderValue(value))
    .map(([label, value]) => `${(label + ':').padEnd(LABEL_PAD_WIDTH)}${value}`)
}

/**
 * Monta o conteúdo completo do .txt: título, cabeçalho de metadados e todas
 * as falas, na ordem de chegada do backend (nunca reordenar — timestamp
 * reseta por sessão, ver detail-tab-transcricao.vue).
 * @param {Object} params { transcript, dataFormatada, duracao }
 * @returns {String}
 */
export const buildTranscricaoTxt = ({
  transcript,
  dataFormatada,
  duracao,
} = {}) => {
  const safeTranscript = transcript || {}
  const titleLine =
    safeTranscript.title || safeTranscript.meeting_id || TITLE_FALLBACK
  const separator = SEPARATOR_CHAR.repeat(SEPARATOR_WIDTH)
  const headerLines = buildTranscricaoHeaderLines({
    transcript: safeTranscript,
    dataFormatada,
    duracao,
  })
  const segmentLines = (safeTranscript.segments || []).map(formatSegmentLine)

  return [titleLine, separator, ...headerLines, separator, '', ...segmentLines].join(
    LINE_BREAK
  )
}

// Ausente/inválido → sufixo omitido (AC-8). Usa parseBackendDateTime (já usado
// pelo resto do módulo) em vez de aawzDate(...).format('YYYY-MM-DD'): o
// formatDate da lib lê ano/mês/dia por getters UTC mas o Date é construído em
// horário local (parseDate.js), então a partir das 21h em BRT o dia avança
// (mesmo bug documentado em ai-framework/utils/dates.js:16-19). Lendo o Date
// de volta com getters locais evita a mistura de referenciais.
const buildFileDateSuffix = (startTime) => {
  const parsed = parseBackendDateTime(startTime)
  if (!parsed) return ''
  const date = parsed.toDate()
  const pad = (n) => String(n).padStart(2, '0')
  return `-${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Monta o nome do arquivo (sem extensão — downloadTxt anexa '.txt').
 * @param {Object} transcript
 * @returns {String}
 */
export const buildTranscricaoFileName = (transcript) => {
  const safeTranscript = transcript || {}
  const slug = slugifyTitle(safeTranscript.title || safeTranscript.meeting_id)
  const dateSuffix = buildFileDateSuffix(safeTranscript.start_time)
  return `${FILE_NAME_PREFIX}-${slug}${dateSuffix}`
}

/**
 * Gera o .txt da transcrição completa e dispara o download.
 * @param {Object} params { transcript, dataFormatada, duracao }
 */
export const baixarTranscricaoTxt = ({
  transcript,
  dataFormatada,
  duracao,
} = {}) => {
  const content = buildTranscricaoTxt({ transcript, dataFormatada, duracao })
  const fileName = buildTranscricaoFileName(transcript)
  downloadTxt({ content, fileName })
}
